import React, { useCallback, useEffect, useState } from 'react';
import { Table, Tag, Space, Button, Popconfirm, Tooltip, Typography, message, Empty } from 'antd';
import { StopOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import abuseService from '../../services/abuseService';

const { Text } = Typography;

const statusColor = { active: 'green', suspended: 'red', pending: 'gold', locked: 'red', inactive: 'default' };

/** Accounts sharing a device or IP prefix with this user (last 90 days) + one-click ban. */
const LinkedAccounts = ({ userId, onChanged }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState([]);
    const [banning, setBanning] = useState(false);

    const load = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const r = await abuseService.getLinked(userId);
            if (r?.success) {
                setData(r.data);
                setSelected((r.data.linked || []).filter((u) => u.by_device && u.status !== 'suspended' && u.role !== 'admin').map((u) => u.id));
            }
        } catch (e) {
            message.error(e?.error || t('Failed to load'));
        } finally {
            setLoading(false);
        }
    }, [userId, t]);

    useEffect(() => { load(); }, [load]);

    const ban = async () => {
        setBanning(true);
        try {
            const r = await abuseService.banLinked(userId, selected);
            if (r?.success) {
                message.success(t('{{count}} accounts suspended', { count: (r.data?.suspended || []).length }));
                load();
                if (onChanged) onChanged();
            }
        } catch (e) {
            message.error(e?.error || t('Operation failed'));
        } finally {
            setBanning(false);
        }
    };

    if (!data) return loading ? null : <Empty />;

    const linkedColumns = [
        {
            title: t('User'), key: 'user',
            render: (_, u) => (
                <Space direction="vertical" size={0}>
                    <Text>#{u.id} {u.username}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{u.email}</Text>
                </Space>
            ),
        },
        { title: t('Status'), dataIndex: 'status', width: 100, render: (s) => <Tag color={statusColor[s] || 'default'}>{s}</Tag> },
        {
            title: t('Shared'), key: 'shared', width: 220,
            render: (_, u) => (
                <Space wrap size={4}>
                    {u.shared_devices > 0 && <Tag color="red">{t('Device')} ×{u.shared_devices}</Tag>}
                    {u.shared_prefixes.map((p) => <Tag key={p}>{p}</Tag>)}
                </Space>
            ),
        },
        { title: t('Claims'), dataIndex: 'claims', width: 80 },
        { title: t('Registered'), dataIndex: 'created_at', width: 160 },
    ];

    const deviceColumns = [
        {
            title: t('Device'), dataIndex: 'device_id', width: 130,
            render: (v, d) => (v ? (
                <Tooltip title={d.components ? JSON.stringify(d.components) : v}>
                    <Text code>{v.slice(0, 12)}</Text>
                </Tooltip>
            ) : '-'),
        },
        { title: 'IP', dataIndex: 'ip', width: 140 },
        {
            title: t('Network'), key: 'net',
            render: (_, d) => (
                <Space size={4} wrap>
                    <Text type="secondary" style={{ fontSize: 12 }}>{d.asn ? `AS${d.asn} ${d.asn_org || ''}` : d.ip_prefix}</Text>
                    {!!d.is_datacenter && <Tag color="orange">{t('Datacenter/VPN')}</Tag>}
                </Space>
            ),
        },
        { title: t('Event'), dataIndex: 'event', width: 90 },
        { title: t('Hits'), dataIndex: 'hits', width: 60 },
        { title: t('Last seen'), dataIndex: 'last_seen', width: 160 },
    ];

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Table
                size="small"
                rowKey="id"
                loading={loading}
                columns={linkedColumns}
                dataSource={data.linked}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                rowSelection={{
                    selectedRowKeys: selected,
                    onChange: setSelected,
                    getCheckboxProps: (u) => ({ disabled: u.role === 'admin' || u.status === 'suspended' }),
                }}
                locale={{ emptyText: t('No linked accounts in the last {{days}} days', { days: data.days }) }}
                title={() => (
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Text strong>{t('Linked accounts')}</Text>
                        <Popconfirm
                            title={t('Suspend {{count}} selected accounts?', { count: selected.length })}
                            onConfirm={ban}
                            okText={t('Suspend')}
                            okButtonProps={{ danger: true }}
                            cancelText={t('Cancel')}
                            disabled={!selected.length}
                        >
                            <Button danger size="small" icon={<StopOutlined />} loading={banning} disabled={!selected.length}>
                                {t('Ban selected')}
                            </Button>
                        </Popconfirm>
                    </Space>
                )}
            />
            <Table
                size="small"
                rowKey="id"
                columns={deviceColumns}
                dataSource={data.devices}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                title={() => <Text strong>{t('Devices')}</Text>}
                locale={{ emptyText: t('No data') }}
                scroll={{ x: 760 }}
            />
        </Space>
    );
};

export default LinkedAccounts;
